import { useState, useEffect } from "react";
import { ChevronUp, Shield, Mail, ExternalLink, FileText } from "lucide-react";

const TermsOfService = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="pp-page" data-testid="terms-of-service-page">
      <main className="pp-main" data-testid="terms-of-service-content">
        <article className="pp-article">
          {/* Header */}
          <header className="pp-header" data-testid="terms-of-service-header">
            <div className="pp-header-icon pp-header-icon--terms">
              <FileText size={28} strokeWidth={1.5} />
            </div>
            <h1 className="pp-title">Terms of Service</h1>
            <p className="pp-app-name">Hearing Decibel Pro</p>
            <p className="pp-last-updated">Last updated: April 19, 2026</p>
          </header>

          {/* 1. Agreement */}
          <section id="agreement" className="pp-section" data-testid="section-agreement">
            <h2 className="pp-section-heading">1. Agreement to These Terms</h2>
            <p>
              These Terms of Service ("Terms") form a legally binding agreement between you ("you"
              or "User") and <strong>Radhesh Gowd</strong>, an individual based in Kerala, India
              ("we," "our," or "us"), governing your use of the Hearing Decibel Pro mobile
              application (the "App") and any services provided through it.
            </p>
            <p>
              By downloading, installing, or using the App, you confirm that you have read,
              understood, and agree to be bound by these Terms. If you do not agree, do not
              install or use the App.
            </p>
            <p>
              These Terms apply in addition to Apple Inc.'s standard End User License Agreement
              for applications distributed through the App Store. Where these Terms and Apple's
              standard EULA conflict, these Terms prevail as between you and us, except where
              Apple's EULA is required by Apple's policies to govern a specific issue.
            </p>
          </section>

          {/* 2. Eligibility */}
          <section id="eligibility" className="pp-section" data-testid="section-eligibility">
            <h2 className="pp-section-heading">2. Eligibility</h2>
            <p>
              You must be at least <strong>13 years old</strong> to use the App (or the minimum
              age at which you can lawfully enter into a binding agreement in your jurisdiction,
              whichever is higher). If you are under 18, you represent that your parent or legal
              guardian has reviewed and agreed to these Terms on your behalf.
            </p>
            <p>
              The App is not intended for use by persons under 13. Do not use the App if you are
              under this age.
            </p>
          </section>

          {/* 3. Medical Disclaimer */}
          <section id="medical-disclaimer" className="pp-section" data-testid="section-tos-medical">
            <h2 className="pp-section-heading">
              3. The App Is a Screening and Educational Tool, Not a Medical Device
            </h2>
            <div className="pp-disclaimer-box" data-testid="tos-medical-disclaimer-box">
              <p style={{ marginBottom: "12px" }}>
                <strong>This section is important. Please read it carefully.</strong>
              </p>
              <p>
                The App is designed to help you estimate your hearing ability, monitor
                environmental noise exposure, and become more aware of your auditory health. It
                is a consumer wellness tool, not a medical device, and is not intended to
                diagnose, treat, cure, or prevent any disease or medical condition.
              </p>
            </div>
            <p>Specifically, you acknowledge and agree that:</p>
            <p>
              <strong>(a)</strong> Hearing test results produced by the App are not a clinical
              audiogram. They are screening estimates generated using consumer earbuds or
              headphones in uncontrolled environments, which materially differ from the calibrated
              equipment, sound-isolated booths, and audiologist-supervised procedures used in
              clinical hearing evaluations.
            </p>
            <p>
              <strong>(b)</strong> Decibel meter readings are produced using your device's
              built-in microphone and depend on factors including microphone quality, physical
              orientation, ambient conditions, and individual device variance. Readings may differ
              from professional sound-level meters by several decibels and should not be relied
              upon for regulatory compliance, occupational safety decisions, or legal evidence.
            </p>
            <p>
              <strong>(c)</strong> Noise exposure tracking provides a general estimate of sound
              exposure based on decibel meter readings and time. It does not account for factors a
              clinical assessment would consider, including bone-conducted sound, personal hearing
              protection, or individual susceptibility.
            </p>
            <p>
              <strong>(d)</strong> If you have any concern about your hearing, or if the App
              suggests hearing loss of any degree, consult a licensed audiologist or qualified
              healthcare professional. Do not delay seeking professional evaluation based on App
              results. A normal App result does not rule out hearing loss, and an abnormal App
              result is not a diagnosis.
            </p>
            <p>
              <strong>(e)</strong> The App is not a substitute for, and does not provide, medical
              advice, professional audiological evaluation, hearing aid fitting, or any other
              service that requires a licensed healthcare practitioner.
            </p>
          </section>

          {/* 4. Account and Local Data */}
          <section id="account-data" className="pp-section" data-testid="section-account-data">
            <h2 className="pp-section-heading">4. Your Account and Local Data</h2>
            <p>
              The App is a <strong>local-first product</strong>. Your hearing test results, noise
              exposure data, and app preferences are stored on your device.
            </p>
            <p>
              <strong>(a)</strong> If you choose to sign in with Apple, a stable anonymous
              identifier and, if you choose to share it, your display name are stored in your
              device's Keychain. We do not create or maintain server-side user accounts.
            </p>
            <p>
              <strong>(b)</strong> You are responsible for maintaining the security of your
              device, including passcodes, biometric authentication, and iCloud security. We are
              not responsible for data loss resulting from device loss, theft, uninstallation,
              damage, or Apple iCloud-related issues.
            </p>
            <p>
              <strong>(c)</strong> You may delete all App data at any time using the "Delete
              Account" or "Clear All Data" options in Settings, or by uninstalling the App.
              Deletion is permanent and cannot be undone.
            </p>
            <p>
              <strong>(d)</strong> We do not operate a backup service. If you uninstall the App
              or reset your device without exporting your data first, your data will be
              permanently lost.
            </p>
          </section>

          {/* 5. Subscriptions and Purchases */}
          <section id="subscriptions" className="pp-section" data-testid="section-subscriptions">
            <h2 className="pp-section-heading">5. Subscriptions and Purchases</h2>
            <p>
              The App offers optional paid features ("Hearing Pro") through the following
              purchase options:
            </p>
            <ul className="pp-list">
              <li>
                <strong>Monthly subscription</strong> — billed monthly, auto-renewing
              </li>
              <li>
                <strong>Annual subscription</strong> — billed annually, auto-renewing
              </li>
              <li>
                <strong>Lifetime purchase</strong> — one-time non-consumable purchase granting
                permanent access
              </li>
            </ul>
            <p>
              Current prices are displayed in the App and on the App Store at the time of
              purchase, in your local currency.
            </p>

            <h3 className="pp-subsection-heading">5.1 Auto-Renewing Subscriptions</h3>
            <p>
              Monthly and Annual subscriptions auto-renew at the end of each billing period
              unless cancelled at least 24 hours before the end of the current period. Payment is
              charged to your Apple ID account on confirmation of purchase and at the start of
              each renewal period.
            </p>
            <p>
              You can manage or cancel your subscription at any time through Settings &gt; Apple
              ID &gt; Subscriptions on your device, or through the App Store.
            </p>

            <h3 className="pp-subsection-heading">5.2 Free Trial</h3>
            <p>
              Monthly and Annual subscriptions include a <strong>3-day free trial</strong> for
              first-time subscribers, subject to Apple's eligibility rules. At the end of the
              trial, the subscription auto-renews at the standard price unless cancelled at least
              24 hours before the trial ends. You can cancel at any time during the trial without
              charge.
            </p>

            <h3 className="pp-subsection-heading">5.3 Lifetime Purchase</h3>
            <p>
              The Lifetime purchase is a one-time, non-consumable purchase. It is not a
              subscription and does not auto-renew. It grants access to all current Hearing Pro
              features for the lifetime of the App.
            </p>
            <p>
              "Lifetime" refers to the ongoing availability of the App itself. We reserve the
              right to discontinue the App, Hearing Pro features, or specific functionality at
              any time. In such cases, you agree that "lifetime access" will terminate upon
              discontinuation, and you will have no claim for refund or compensation beyond what
              Apple's App Store policies provide.
            </p>

            <h3 className="pp-subsection-heading">5.4 Payment Processing and Refunds</h3>
            <p>
              All purchases are processed by Apple through the App Store. We do not process
              payments directly and do not have access to your payment details.
            </p>
            <p>
              Refund requests must be directed to Apple, not to us. You can request a refund via{" "}
              <a
                href="https://reportaproblem.apple.com"
                target="_blank"
                rel="noopener noreferrer"
                className="pp-link"
                data-testid="apple-refund-link"
              >
                reportaproblem.apple.com
                <ExternalLink size={12} className="pp-external-icon" />
              </a>{" "}
              or through Apple Support. Apple applies its own refund policies, which we do not
              control.
            </p>
            <p>
              Where we are legally required under Indian or other applicable consumer law to
              provide a direct refund, we will do so in accordance with that law.
            </p>

            <h3 className="pp-subsection-heading">5.5 Price and Feature Changes</h3>
            <p>
              We reserve the right to change subscription prices, add or remove features, or
              restructure subscription tiers at any time. Price changes for existing auto-renewing
              subscribers will be communicated and require your consent as required by Apple's
              policies and applicable law. Continued use of a subscription after a disclosed price
              change constitutes acceptance of the new price.
            </p>

            <h3 className="pp-subsection-heading">
              5.6 No Warranty That Hearing Pro Features Will Continue Indefinitely
            </h3>
            <p>
              While we intend to maintain all Hearing Pro features, we do not guarantee that any
              specific feature (including but not limited to PDF export, advanced training levels,
              exposure tracking, or saved hearing test history) will continue to be available. We
              may modify, degrade, or remove features without prior notice where operationally
              necessary.
            </p>
          </section>

          {/* 6. Permitted Use */}
          <section id="permitted-use" className="pp-section" data-testid="section-permitted-use">
            <h2 className="pp-section-heading">6. Permitted Use</h2>
            <p>
              You may use the App for your own personal, non-commercial purposes, subject to
              these Terms. You may not:
            </p>
            <div className="pp-legal-list">
              <p>
                <strong>(a)</strong> Reverse engineer, decompile, or disassemble the App, except
                to the extent permitted by applicable law
              </p>
              <p>
                <strong>(b)</strong> Modify, adapt, translate, or create derivative works based
                on the App
              </p>
              <p>
                <strong>(c)</strong> Sell, rent, lease, sublicense, distribute, or redistribute
                the App or any portion of it
              </p>
              <p>
                <strong>(d)</strong> Use the App or its output to provide paid audiology services,
                clinical assessments, or any service that a reasonable person would expect to be
                performed by a licensed healthcare practitioner
              </p>
              <p>
                <strong>(e)</strong> Use the App in a manner that violates applicable law
              </p>
              <p>
                <strong>(f)</strong> Circumvent, disable, or otherwise interfere with security
                features of the App, including subscription verification
              </p>
              <p>
                <strong>(g)</strong> Use automation, scripts, or bots to interact with the App
              </p>
              <p>
                <strong>(h)</strong> Share a Hearing Pro subscription or Lifetime purchase with
                persons outside your Apple Family Sharing group (where enabled)
              </p>
            </div>
            <p>
              We may suspend your access to paid features if we reasonably believe you have
              violated these Terms, subject to applicable consumer protection law.
            </p>
          </section>

          {/* 7. Intellectual Property */}
          <section id="intellectual-property" className="pp-section" data-testid="section-ip">
            <h2 className="pp-section-heading">7. Intellectual Property</h2>
            <p>
              The App, including all software, graphics, text, user interface design, audio
              content, icons, and documentation, is owned by us or our licensors and is protected
              by copyright, trademark, and other applicable laws.
            </p>
            <p>
              These Terms grant you a limited, personal, non-exclusive, non-transferable,
              revocable license to use the App, subject to Apple's standard EULA and these Terms.
              No other rights are granted.
            </p>
            <p>
              The name "Hearing Decibel Pro" and the App's icon and visual identity are our
              trademarks. You may not use them without our written permission, except for
              fair-use references (for example, linking to the App Store page).
            </p>
          </section>

          {/* 8. Third-Party Services */}
          <section id="third-party" className="pp-section" data-testid="section-third-party">
            <h2 className="pp-section-heading">8. Third-Party Services</h2>
            <p>The App uses the following third-party services to function:</p>
            <p>
              <strong>(a) Apple App Store and Apple services</strong> — for distribution, in-app
              purchases, Sign in with Apple, HealthKit integration, and advertising attribution.
              Your use of these services is subject to Apple's terms and privacy policies.
            </p>
            <p>
              <strong>(b) RevenueCat, Inc.</strong> — for subscription management and purchase
              validation. See{" "}
              <a
                href="https://www.revenuecat.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="pp-link"
                data-testid="revenuecat-terms-link"
              >
                revenuecat.com/terms
                <ExternalLink size={12} className="pp-external-icon" />
              </a>{" "}
              for their terms.
            </p>
            <p>
              We are not responsible for third-party services, their availability, or changes they
              make. If a third-party service becomes unavailable and this affects App
              functionality, we will make reasonable efforts to adapt, but we do not guarantee
              uninterrupted service.
            </p>
          </section>

          {/* 9. Privacy */}
          <section id="privacy" className="pp-section" data-testid="section-tos-privacy">
            <h2 className="pp-section-heading">9. Privacy</h2>
            <p>
              Our Privacy Policy, available at{" "}
              <a href="/hearingdecibelpro/privacy" className="pp-link" data-testid="privacy-policy-link-from-tos">
                radships.com/hearingdecibelpro/privacy
              </a>
              , describes how we handle data. It forms part of these Terms by reference. By
              agreeing to these Terms, you also agree to the practices described in the Privacy
              Policy.
            </p>
          </section>

          {/* 10. Service Availability */}
          <section id="service-availability" className="pp-section" data-testid="section-availability">
            <h2 className="pp-section-heading">10. Service Availability and Modifications</h2>
            <p>
              <strong>(a)</strong> We provide the App on an "as-is" and "as-available" basis. We
              do not guarantee that the App will be available at all times, that it will be free
              of errors or bugs, or that defects will be corrected.
            </p>
            <p>
              <strong>(b)</strong> We may modify, suspend, or discontinue the App or any feature
              at any time, with or without notice.
            </p>
            <p>
              <strong>(c)</strong> We may release updates that change the App's behavior,
              features, or compatibility. You are responsible for installing updates as provided
              through the App Store.
            </p>
          </section>

          {/* 11. Limitation of Liability */}
          <section id="liability" className="pp-section" data-testid="section-liability">
            <h2 className="pp-section-heading">11. Limitation of Liability</h2>
            <div className="pp-highlight-box" data-testid="liability-highlight">
              <p>
                <strong>
                  Please read this section carefully. It limits our legal responsibility to you.
                </strong>
              </p>
            </div>
            <p>To the maximum extent permitted by applicable law:</p>
            <p>
              <strong>(a)</strong> The App is provided without warranties of any kind, whether
              express or implied, including implied warranties of merchantability, fitness for a
              particular purpose, non-infringement, or accuracy.
            </p>
            <p>
              <strong>(b)</strong> We are not liable for indirect, incidental, consequential,
              special, exemplary, or punitive damages, including loss of data, loss of profit,
              loss of business opportunity, personal injury, or emotional distress arising from
              your use of or inability to use the App.
            </p>
            <p>
              <strong>(c)</strong> In particular, we are not liable for:
            </p>
            <ul className="pp-list">
              <li>Any medical decision you make based on information from the App</li>
              <li>
                Any harm arising from reliance on the App's hearing test results, decibel
                readings, or exposure tracking
              </li>
              <li>
                Loss of hearing test history, exposure data, or other local data due to device
                issues, uninstallation, or failure to back up
              </li>
              <li>
                Any consequence of Apple or RevenueCat service changes, outages, or errors
              </li>
            </ul>
            <p>
              <strong>(d)</strong> Where liability cannot be excluded as a matter of law, our
              total cumulative liability to you for all claims arising out of or relating to the
              App or these Terms is limited to the amount you have paid us (via Apple) for the
              App in the twelve months preceding the event giving rise to the claim, or{" "}
              <strong>&#8377;500 (Indian Rupees)</strong>, whichever is greater.
            </p>
            <p>
              <strong>(e)</strong> Nothing in these Terms limits liability that cannot lawfully be
              limited, including liability for gross negligence, willful misconduct, or death or
              personal injury caused by our negligence where applicable law does not permit such
              limitation.
            </p>
          </section>

          {/* 12. Indemnification */}
          <section id="indemnification" className="pp-section" data-testid="section-indemnification">
            <h2 className="pp-section-heading">12. Indemnification</h2>
            <p>
              You agree to indemnify and hold us harmless from any claims, damages, losses,
              liabilities, costs, or expenses (including reasonable legal fees) arising from:
            </p>
            <div className="pp-legal-list">
              <p>
                <strong>(a)</strong> Your violation of these Terms
              </p>
              <p>
                <strong>(b)</strong> Your misuse of the App, including using it in a way these
                Terms prohibit
              </p>
              <p>
                <strong>(c)</strong> Your reliance on App output in a clinical, occupational, or
                regulatory context
              </p>
            </div>
          </section>

          {/* 13. Termination */}
          <section id="termination" className="pp-section" data-testid="section-termination">
            <h2 className="pp-section-heading">13. Termination</h2>
            <p>
              <strong>(a)</strong> You may stop using the App at any time by uninstalling it.
            </p>
            <p>
              <strong>(b)</strong> We may terminate or suspend your access to the App (including
              paid features) if you materially breach these Terms, subject to applicable law.
            </p>
            <p>
              <strong>(c)</strong> On termination, Sections 3 (medical disclaimer), 7
              (intellectual property), 11 (limitation of liability), 12 (indemnification), 14
              (governing law), and 15 (dispute resolution) survive.
            </p>
          </section>

          {/* 14. Governing Law */}
          <section id="governing-law" className="pp-section" data-testid="section-governing-law">
            <h2 className="pp-section-heading">14. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with the{" "}
              <strong>laws of India</strong>, without regard to conflict-of-law principles.
            </p>
            <p>
              This choice of law does not deprive you of any consumer protection rights available
              under the mandatory laws of your country of residence.
            </p>
          </section>

          {/* 15. Dispute Resolution */}
          <section id="dispute-resolution" className="pp-section" data-testid="section-dispute">
            <h2 className="pp-section-heading">15. Dispute Resolution</h2>
            <p>
              <strong>(a)</strong> If you have a dispute or complaint, please first contact us at{" "}
              <a href="mailto:support@radships.com" className="pp-link" data-testid="dispute-email-link">
                support@radships.com
              </a>{" "}
              so we can try to resolve it informally. We will respond within a reasonable time.
            </p>
            <p>
              <strong>(b)</strong> If the dispute cannot be resolved informally, it will be
              submitted to the exclusive jurisdiction of the courts of Kerala, India, except that:
            </p>
            <p>
              <strong>(c)</strong> If you are a consumer residing in a jurisdiction whose laws
              require that disputes be heard locally, you may bring proceedings in your local
              courts, and we may only bring proceedings against you in those courts.
            </p>
            <p>
              <strong>(d)</strong> Nothing in this section prevents either party from seeking
              injunctive relief in any court of competent jurisdiction to protect intellectual
              property rights or confidential information.
            </p>
          </section>

          {/* 16. Changes */}
          <section id="changes" className="pp-section" data-testid="section-tos-changes">
            <h2 className="pp-section-heading">16. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. When we make material changes, we will
              update the "Last updated" date above and, where appropriate, notify you via the App
              or via this URL.
            </p>
            <p>
              Your continued use of the App after the updated Terms take effect constitutes
              acceptance of the updated Terms. If you do not agree to the updated Terms, stop
              using the App.
            </p>
          </section>

          {/* 17. Apple-Specific Provisions */}
          <section id="apple-provisions" className="pp-section" data-testid="section-apple">
            <h2 className="pp-section-heading">17. Apple-Specific Provisions</h2>
            <p>In accordance with Apple's requirements:</p>
            <div className="pp-legal-list">
              <p>
                <strong>(a)</strong> These Terms are concluded between you and us only, and not
                with Apple Inc. Apple is not responsible for the App or its content.
              </p>
              <p>
                <strong>(b)</strong> Apple has no obligation to provide maintenance or support
                services for the App.
              </p>
              <p>
                <strong>(c)</strong> If the App fails to conform to any applicable warranty, you
                may notify Apple, and Apple may refund the purchase price for the App. To the
                maximum extent permitted by applicable law, Apple has no other warranty obligation
                whatsoever with respect to the App.
              </p>
              <p>
                <strong>(d)</strong> We, not Apple, are responsible for addressing any claims you
                or any third party may have relating to the App or your use of it, including
                product liability claims, claims that the App fails to conform to any applicable
                legal or regulatory requirement, and claims arising under consumer protection or
                similar legislation.
              </p>
              <p>
                <strong>(e)</strong> We, not Apple, are responsible for investigating, defending,
                settling, and discharging any third-party intellectual property infringement claim
                relating to the App.
              </p>
              <p>
                <strong>(f)</strong> You represent and warrant that you are not located in a
                country subject to a U.S. Government embargo, or that has been designated by the
                U.S. Government as a "terrorist supporting" country, and that you are not listed
                on any U.S. Government list of prohibited or restricted parties.
              </p>
              <p>
                <strong>(g)</strong> Apple and its subsidiaries are third-party beneficiaries of
                these Terms and, upon your acceptance of these Terms, will have the right (and
                will be deemed to have accepted the right) to enforce these Terms against you as
                a third-party beneficiary of them.
              </p>
            </div>
          </section>

          {/* 18. Severability */}
          <section id="severability" className="pp-section" data-testid="section-severability">
            <h2 className="pp-section-heading">18. Severability and Entire Agreement</h2>
            <p>
              <strong>(a)</strong> If any provision of these Terms is held to be unenforceable or
              invalid, that provision will be limited or eliminated to the minimum extent
              necessary, and the remaining provisions will remain in full force and effect.
            </p>
            <p>
              <strong>(b)</strong> These Terms, together with the Privacy Policy, constitute the
              entire agreement between you and us regarding the App and supersede any prior
              agreements.
            </p>
            <p>
              <strong>(c)</strong> Our failure to enforce any right or provision of these Terms is
              not a waiver of that right or provision.
            </p>
          </section>

          {/* 19. Contact */}
          <section id="contact" className="pp-section" data-testid="section-tos-contact">
            <h2 className="pp-section-heading">19. Contact</h2>
            <p>Questions about these Terms can be sent to:</p>
            <div className="pp-contact-card" data-testid="tos-contact-card">
              <p className="pp-contact-name">Radhesh Gowd</p>
              <a
                href="mailto:support@radships.com"
                className="pp-contact-email"
                data-testid="tos-contact-email-link"
              >
                <Mail size={16} />
                support@radships.com
              </a>
              <p className="pp-contact-location">Based in: India</p>
            </div>
          </section>

          {/* Footer */}
          <footer className="pp-footer" data-testid="terms-of-service-footer">
            <p>&copy; {new Date().getFullYear()} Radhesh Gowd. All rights reserved.</p>
          </footer>
        </article>
      </main>

      {/* Back to top */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="pp-back-to-top"
          aria-label="Back to top"
          data-testid="tos-back-to-top-button"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
};

export default TermsOfService;
